import React, { useState, useEffect, useRef } from "react";
import { FaUserMd, FaCalendarCheck, FaCheckCircle, FaFilter, FaUserPlus, FaComments, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../styles/find-practitioner.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BookAppointmentModal from './BookAppointmentModal';
import LoginModal from '../components/LoginModal';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LogoCopy } from '../assets/Logo copy.svg';
import InviteModal from '../components/InviteModal';
import AppointmentRequestModal from '../components/AppointmentRequestModal';
import { useChatWidget } from '../context/ChatWidgetContext';

// Simple star rating component
const Rating = ({ value = 0 }) => (
  <span className="stars">
    {[1, 2, 3, 4, 5].map((i) => (i <= value ? "★" : "☆"))}
  </span>
);

const FindPractitioners = () => {
  const { openLargeChat } = useChatWidget();
  const [searchResults, setSearchResults] = useState({
    name: [],
    location: [],
    specialty: []
  });
  const [activeSearch, setActiveSearch] = useState(null);
  const [searchQuery, setSearchQuery] = useState({
    name: '',
    location: '',
    specialty: ''
  });
  const [isLoading, setIsLoading] = useState({
    name: false,
    location: false,
    specialty: false,
    initial: true,
    loadMore: false
  });
  const [practitioners, setPractitioners] = useState([]);
  const [displayedPractitioners, setDisplayedPractitioners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0,
    has_next: false,
    has_previous: false,
    offset: 0,
    limit: 10
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedInsurance, setSelectedInsurance] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const doctorsPerPage = 10;
  const timeoutRefs = {
    name: useRef(null),
    location: useRef(null),
    specialty: useRef(null)
  };
  const navigate = useNavigate();
  const loadMoreRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteDoctor, setInviteDoctor] = useState(null);
  const [appointmentRequestModalOpen, setAppointmentRequestModalOpen] = useState(false);
  const [appointmentRequestDoctor, setAppointmentRequestDoctor] = useState(null);
  const [bookAppointmentModalOpen, setBookAppointmentModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [practitionerToRedirect, setPractitionerToRedirect] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sliderRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);

  const countryCities = {
    "United States": ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix", "Philadelphia", "San Antonio", "San Diego", "Dallas", "San Jose"],
    "Canada": ["Toronto", "Vancouver", "Montreal", "Calgary", "Edmonton", "Ottawa", "Winnipeg", "Quebec City", "Hamilton", "Kitchener"],
    "United Kingdom": ["London", "Manchester", "Birmingham", "Leeds", "Glasgow", "Liverpool", "Newcastle", "Sheffield", "Bristol", "Edinburgh"],
    "Pakistan": ["Karachi", "Lahore", "Islamabad", "Faisalabad", "Rawalpindi", "Multan", "Hyderabad", "Gujranwala", "Peshawar", "Quetta"],
    "UAE": ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Al Ain", "Khor Fakkan", "Dibba Al-Fujairah"]
  };

  // Fetch practitioners based on location and country
  const fetchPractitionersByLocation = async (location) => {
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      // Get cities for the selected country
      const cities = countryCities[location] || [];
      
      // Create an array of promises for all location searches
      const searchPromises = [
        // Search for the country itself
        fetch(`https://directory.virtualtriage.ai/doctors/search/location/?location=${location}`),
        // Search for each city in the country
        ...cities.map(city => 
          fetch(`https://directory.virtualtriage.ai/doctors/search/location/?location=${city}`)
        )
      ];

      // Execute all searches in parallel
      const responses = await Promise.all(searchPromises);
      const results = await Promise.all(responses.map(res => res.json()));

      // Combine and deduplicate results
      const uniqueDoctorIds = new Set();
      const combinedDoctors = [];

      // Process all results
      results.forEach(result => {
        const doctors = result.data.data || [];
        doctors.forEach(doc => {
          if (!uniqueDoctorIds.has(doc._id)) {
            uniqueDoctorIds.add(doc._id);
            combinedDoctors.push(doc);
          }
        });
      });

      setPractitioners(combinedDoctors);
      setDisplayedPractitioners(combinedDoctors);
      setPagination({
        total_count: combinedDoctors.length,
        page: 1,
        page_size: 10,
        total_pages: Math.ceil(combinedDoctors.length / 10),
        has_next: combinedDoctors.length > 10,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching practitioners by location:', error);
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Helper to fetch all doctors (default list)
  const fetchAllDoctors = async () => {
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      const response = await fetch('https://directory.virtualtriage.ai/doctors/?limit=10&offset=0');
      const data = await response.json();
      setPractitioners(data.data.data || []);
      setDisplayedPractitioners(data.data.data || []);
      setPagination(data.data.pagination || {
        total_count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
        has_next: false,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Handle location selection from sidebar
  const handleLocationSelect = (location) => {
    if (selectedLocation === location) {
      setSelectedLocation(null);
      fetchAllDoctors();
      return;
    }
    setSelectedLocation(location);
    resetPagination();
    fetchPractitionersByLocation(location);
  };

  // Fetch initial practitioners on component mount
  useEffect(() => {
    const fetchInitialPractitioners = async () => {
      try {
        const response = await fetch('https://directory.virtualtriage.ai/doctors/?limit=10&offset=0');
        const data = await response.json();
        setPractitioners(data.data.data || []);
        setDisplayedPractitioners(data.data.data || []);
        setPagination(data.data.pagination || {
          total_count: 0,
          page: 1,
          page_size: 10,
          total_pages: 0,
          has_next: false,
          has_previous: false,
          offset: 0,
          limit: 10
        });
        setCurrentPage(1);
      } catch (error) {
        console.error('Error fetching initial practitioners:', error);
        setPractitioners([]);
        setDisplayedPractitioners([]);
      } finally {
        setIsLoading(prev => ({ ...prev, initial: false }));
      }
    };

    fetchInitialPractitioners();
  }, []);

  const handleLoadMore = async () => {
    if (!pagination.has_next || isLoading.loadMore) return;
    
    setIsLoading(prev => ({ ...prev, loadMore: true }));
    try {
      const nextOffset = currentPage * 10; // Calculate offset based on current page
      let url = '';
      
      // Handle different filter scenarios
      if (selectedRating) {
        url = `https://directory.virtualtriage.ai/doctors/popular/ratings/?rating=${Number(selectedRating)}&limit=10&offset=${nextOffset}`;
      } else if (selectedLanguage) {
        url = `https://directory.virtualtriage.ai/doctors/languages/?language=${encodeURIComponent(selectedLanguage)}&limit=10&offset=${nextOffset}`;
      } else if (selectedInsurance) {
        url = `https://directory.virtualtriage.ai/doctors/insurance/?insurance=Yes&limit=10&offset=${nextOffset}`;
      } else if (selectedSpecialty) {
        url = `https://directory.virtualtriage.ai/doctors/specialities/?speciality=${encodeURIComponent(selectedSpecialty)}&limit=10&offset=${nextOffset}`;
      } else {
        // Default case - use the new API
        url = `https://directory.virtualtriage.ai/doctors/?limit=10&offset=${nextOffset}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      const newPractitioners = data.data.data || [];
      
      // Append new practitioners to existing ones
      setPractitioners(prev => [...prev, ...newPractitioners]);
      setDisplayedPractitioners(prev => [...prev, ...newPractitioners]);
      setPagination(data.data.pagination || pagination);
      setCurrentPage(currentPage + 1);
    } catch (error) {
      console.error('Error loading more practitioners:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, loadMore: false }));
    }
  };

  // Helper function to reset pagination state
  const resetPagination = () => {
    setPagination({
      total_count: 0,
      page: 1,
      page_size: 10,
      total_pages: 0,
      has_next: false,
      has_previous: false,
      offset: 0,
      limit: 10
    });
    setCurrentPage(1);
  };

  const performSearch = async (type, value) => {
    if (!value.trim()) {
      setSearchResults(prev => ({ ...prev, [type]: [] }));
      setIsLoading(prev => ({ ...prev, [type]: false }));
      return;
    }

    setIsLoading(prev => ({ ...prev, [type]: true }));
    try {
      let response;
      switch (type) {
        case 'name':
          response = await fetch(`https://directory.virtualtriage.ai/doctors/search/?query=${encodeURIComponent(value)}&limit=10&offset=0`);
          break;
        case 'location':
          // Check if the input matches any country
          const matchingCountry = Object.keys(countryCities).find(
            country => country.toLowerCase() === value.toLowerCase()
          );
          
          if (matchingCountry) {
            // If it's a country, search for country and all its cities
            const cities = countryCities[matchingCountry] || [];
            const searchPromises = [
              fetch(`https://directory.virtualtriage.ai/doctors/search/location/?location=${encodeURIComponent(matchingCountry)}`),
              ...cities.map(city => 
                fetch(`https://directory.virtualtriage.ai/doctors/search/location/?location=${encodeURIComponent(city)}`)
              )
            ];

            const responses = await Promise.all(searchPromises);
            const results = await Promise.all(responses.map(res => res.json()));

            // Combine and deduplicate results
            const uniqueDoctorIds = new Set();
            const combinedDoctors = [];

            results.forEach(result => {
              const doctors = result.data.data || [];
              doctors.forEach(doc => {
                if (!uniqueDoctorIds.has(doc._id)) {
                  uniqueDoctorIds.add(doc._id);
                  combinedDoctors.push(doc);
                }
              });
            });

            setSearchResults(prev => ({ ...prev, [type]: combinedDoctors }));
            setIsLoading(prev => ({ ...prev, [type]: false }));
            return;
          } else {
            // If it's not a country, just search for the location as is
            response = await fetch(`https://directory.virtualtriage.ai/doctors/search/location/?location=${encodeURIComponent(value)}`);
          }
          break;
        case 'specialty':
          // First, get all practitioners
          const allDoctorsResponse = await fetch('https://directory.virtualtriage.ai/doctors/?limit=10&offset=0');
          const allDoctorsData = await allDoctorsResponse.json();
          const allDoctors = allDoctorsData.data.data || [];

          // Filter doctors based on specialty or subspecialty
          const filteredDoctors = allDoctors.filter(doc => {
            const specialties = [
              ...(doc.specialties || []),
              ...(doc.subspecialties || []),
              doc.specialty || ''
            ].map(s => (s || '').toLowerCase());
            
            const searchTerm = value.toLowerCase();
            return specialties.some(s => s.includes(searchTerm));
          });

          setSearchResults(prev => ({ ...prev, [type]: filteredDoctors }));
          setIsLoading(prev => ({ ...prev, [type]: false }));
          return;
        default:
          return;
      }

      const data = await response.json();
      setSearchResults(prev => ({ ...prev, [type]: data.data.data || [] }));
    } catch (error) {
      console.error(`Error fetching ${type} search results:`, error);
      setSearchResults(prev => ({ ...prev, [type]: [] }));
    } finally {
      setIsLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Filter practitioners by specialty keyword (case-insensitive, partial match)
  const filterBySpecialtyKeyword = (practitionersList, keyword) => {
    if (!keyword.trim()) return practitionersList;
    const lowerKeyword = keyword.trim().toLowerCase();
    return practitionersList.filter(doc => {
      const specialties = [
        ...(doc.specialties || []),
        ...(doc.subspecialties || []),
        doc.specialty || '',
      ].map(s => (s || '').toLowerCase());
      return specialties.some(s => s.includes(lowerKeyword));
    });
  };

  // Update the specialty search handler
  const handleSearch = (type, value) => {
    setSearchQuery(prev => ({ ...prev, [type]: value }));
    setActiveSearch(type);

    // Clear any existing timeout for this type
    if (timeoutRefs[type].current) {
      clearTimeout(timeoutRefs[type].current);
    }

    // Set new timeout for this type
    timeoutRefs[type].current = setTimeout(() => {
      performSearch(type, value);
    }, 1000);
  };

  // Add new function to fetch initial practitioners
  const fetchInitialSearchResults = async (type) => {
    setIsLoading(prev => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/?query=limit=10&offset=0`);
      const data = await response.json();
      // Take only the first 10 practitioners
      const initialResults = (data.data.data || []).slice(0, 10);
      setSearchResults(prev => ({ ...prev, [type]: initialResults }));
    } catch (error) {
      console.error('Error fetching initial search results:', error);
      setSearchResults(prev => ({ ...prev, [type]: [] }));
    } finally {
      setIsLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Update the handleSearchFieldClick function
  const handleSearchFieldClick = (type) => {
    setActiveSearch(type);
    const currentValue = searchQuery[type];
    
    if (currentValue.trim()) {
      setIsLoading(prev => ({ ...prev, [type]: true }));
      performSearch(type, currentValue);
    } else {
      // If the field is empty, fetch initial results
      fetchInitialSearchResults(type);
    }
  };

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs).forEach(ref => {
        if (ref.current) {
          clearTimeout(ref.current);
        }
      });
    };
  }, []);

  const handleClickOutside = (e) => {
    if (!e.target.closest('.search-bar-row')) {
      setActiveSearch(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handlePractitionerClick = async (doctorId) => {
    try {
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/${doctorId}`);
      const data = await response.json();
      if (data.data) {
        window.scrollTo(0, 0);
        navigate(`/profile/${doctorId}`, { state: { doctorData: data.data } });
      }
    } catch (error) {
      console.error('Error fetching doctor details:', error);
    }
  };

  const handleBookAppointment = async (practitioner) => {
    console.log("handleBookAppointment called with practitioner:", practitioner);
    
    // Check if user is logged in
    const authToken = localStorage.getItem("authToken");
    
    if (!authToken) {
      console.log("User not logged in, storing practitioner and opening login modal");
      // User is not logged in, store practitioner info and open login modal
      setPractitionerToRedirect(practitioner);
      setLoginModalOpen(true);
      return;
    }
    
    // User is logged in, proceed with booking
    console.log("User is logged in, proceeding with booking");
    try {
      const response = await fetch("https://portal.virtualtriage.ca/api/v1/patient/appointment/book-appointment/directory/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({
          doctor_email: practitioner.email
        }),
      });
      
      const data = await response.json();
      console.log("Booking API response:", data);
      
      if (data.status) {
        alert("Appointment booked successfully!");
      } else if (data.status === false && data.clinic_id) {
        // Redirect to the website's clinic details page
        navigate(`/clinic-details/${data.clinic_id}`);
      } else {
        alert(data.message || "Failed to book appointment.");
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      alert("Error booking appointment. Please try again later.");
    }
  };

  // Handle successful login and make the booking API call
  const handleLoginSuccess = async (practitioner) => {
    console.log("handleLoginSuccess called with practitioner:", practitioner);
    
    try {
      const authToken = localStorage.getItem("authToken");
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      if (!authToken) {
        console.error("No auth token found after login");
        return;
      }

      // Make the booking API call
      const response = await fetch("https://portal.virtualtriage.ca/api/v1/patient/appointment/book-appointment/directory/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({
          doctor_email: practitioner.email
        }),
      });

      const data = await response.json();
      console.log("API Response after login:", data);

      if (data.status === false && data.clinic_id) {
        // Redirect to the website's clinic details page
        navigate(`/clinic-details/${data.clinic_id}`);
      } else {
        alert(data.message || "Failed to book appointment.");
      }
    } catch (error) {
      console.error("Error booking appointment after login:", error);
      alert("Error booking appointment. Please try again later.");
    }
  };

  // Update the practitioner card rendering in the search results
  const renderPractitionerCard = (doc) => (
    <div 
      key={doc._id} 
      className="main-practitioner-card"
      onClick={() => handlePractitionerClick(doc._id)}
    >
      {/* Instant Book Badge */}
      {doc.status === true && (
        <div className="instant-book-badge">
          ⚡ Instant Book
        </div>
      )}
      {/* Approval Required Badge */}
      {doc.status === false && (
        <div className="approval-required-badge">
          ⏳ Approval Required
        </div>
      )}
      <img
        src={doc.profile_picture_url || process.env.PUBLIC_URL + "/assets/Logo.jpg"}
        alt={doc.full_name}
        className="main-practitioner-img"
        onError={e => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/assets/Logo.jpg"; }}
      />
      <div className="main-practitioner-info">
        <div className="main-practitioner-name">{doc.full_name}</div>
        <div className="main-practitioner-specialty">{doc.subspecialties?.[0] || 'General Practitioner'}</div>
        <div className="main-practitioner-rating">
          Ratings: <Rating value={doc.rating || 0} />
        </div>
        <div className="main-practitioner-actions">
          <button className="main-practitioner-btn"><FaUserMd className="icon-margin-right"/>View Profile</button>
          {doc.status === true ? (
            <button 
              className="main-practitioner-btn book-appointment-btn"
              onClick={e => { 
                e.stopPropagation(); 
                handleBookAppointment(doc);
              }}
            >
              <FaCalendarCheck className="icon-margin-right"/>Book Appointment
            </button>
          ) : doc.status === false ? (
            <button 
              className="main-practitioner-btn book-appointment-btn"
              onClick={e => { 
                e.stopPropagation(); 
                setAppointmentRequestDoctor(doc);
                setAppointmentRequestModalOpen(true);
              }}
            >
              <FaCalendarCheck className="icon-margin-right"/>Book Appointment
            </button>
          ) : doc.status === null || doc.status === undefined ? (
            <button 
              className="main-practitioner-btn invite-btn"
              onClick={e => { 
                e.stopPropagation(); 
                setInviteDoctor(doc); 
                setInviteModalOpen(true); 
              }}
            >
              <FaUserPlus className="icon-margin-right" />Invite
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  // Ratings filter handler
  const handleRatingSelect = async (rating) => {
    if (selectedRating === rating) {
      setSelectedRating(null);
      fetchAllDoctors();
      return;
    }
    setSelectedRating(rating);
    setSelectedLocation(null); // Clear location filter when rating is selected
    setSelectedLanguage(null); // Clear language filter
    setSelectedInsurance(false); // Clear insurance filter
    setSelectedSpecialty(null); // Clear specialty filter
    resetPagination();
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      // Always send a single digit (3 or 4) to the API
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/popular/ratings/?rating=${Number(rating)}&limit=10&offset=0`);
      const data = await response.json();
      setPractitioners(data.data.data || []);
      setDisplayedPractitioners(data.data.data || []);
      setPagination(data.data.pagination || {
        total_count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
        has_next: false,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Languages filter handler
  const handleLanguageSelect = async (language) => {
    if (selectedLanguage === language) {
      setSelectedLanguage(null);
      fetchAllDoctors();
      return;
    }
    setSelectedLanguage(language);
    setSelectedLocation(null); // Clear location filter
    setSelectedRating(null);   // Clear rating filter
    setSelectedInsurance(false); // Clear insurance filter
    setSelectedSpecialty(null); // Clear specialty filter
    resetPagination();
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/languages/?language=${encodeURIComponent(language)}`);
      const data = await response.json();
      setPractitioners(data.data.data || []);
      setDisplayedPractitioners(data.data.data || []);
      setPagination(data.data.pagination || {
        total_count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
        has_next: false,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Insurance filter handler
  const handleInsuranceSelect = async () => {
    if (selectedInsurance) {
      setSelectedInsurance(false);
      fetchAllDoctors();
      return;
    }
    setSelectedInsurance(true);
    setSelectedLocation(null); // Clear location filter
    setSelectedRating(null);   // Clear rating filter
    setSelectedLanguage(null); // Clear language filter
    setSelectedSpecialty(null); // Clear specialty filter
    resetPagination();
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      const response = await fetch('https://directory.virtualtriage.ai/doctors/insurance/?insurance=Yes');
      const data = await response.json();
      // Exclude practitioners with no insurance details
      const filtered = (data.data.data || []).filter(doc => Array.isArray(doc.insurers) && doc.insurers.length > 0);
      setPractitioners(filtered);
      setDisplayedPractitioners(filtered);
      setPagination({
        total_count: filtered.length,
        page: 1,
        page_size: 10,
        total_pages: Math.ceil(filtered.length / 10),
        has_next: filtered.length > 10,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Speciality filter handler
  const handleSpecialtySelect = async (specialty) => {
    if (selectedSpecialty === specialty) {
      setSelectedSpecialty(null);
      fetchAllDoctors();
      return;
    }
    setSelectedSpecialty(specialty);
    setSelectedLocation(null); // Clear location filter
    setSelectedRating(null);   // Clear rating filter
    setSelectedLanguage(null); // Clear language filter
    setSelectedInsurance(false); // Clear insurance filter
    resetPagination();
    setIsLoading(prev => ({ ...prev, initial: true }));
    try {
      const response = await fetch(`https://directory.virtualtriage.ai/doctors/specialities/?speciality=${encodeURIComponent(specialty)}`);
      const data = await response.json();
      setPractitioners(data.data.data || []);
      setDisplayedPractitioners(data.data.data || []);
      setPagination(data.data.pagination || {
        total_count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0,
        has_next: false,
        has_previous: false,
        offset: 0,
        limit: 10
      });
      setCurrentPage(1);
    } catch (error) {
      setPractitioners([]);
      setDisplayedPractitioners([]);
    } finally {
      setIsLoading(prev => ({ ...prev, initial: false }));
    }
  };

  // Add toggle handler
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Add click outside handler
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('sidebar-overlay')) {
      setIsSidebarOpen(false);
    }
  };

  // Auto-scroll functionality
  useEffect(() => {
    const validPractitioners = getValidPractitioners();
    if (validPractitioners.length > 0 && !isPaused) {
      autoScrollIntervalRef.current = setInterval(() => {
        if (!isDragging) {
          setCurrentSlide(prev => {
            const totalSlides = Math.ceil(validPractitioners.slice(0, 15).length / 3);
            const nextSlide = (prev + 1) % totalSlides;
            return nextSlide;
          });
        }
      }, 3000); // Change slide every 3 seconds
    }

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [displayedPractitioners.length, isDragging, isPaused]);

  // Update scroll position when currentSlide changes
  useEffect(() => {
    if (sliderRef.current && !isDragging) {
      const cardWidth = 320 + 20; // card width + gap
      const scrollPosition = currentSlide * cardWidth * 3; // Show 3 cards per slide
      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentSlide, isDragging]);

  // Listen for scroll events to update current slide
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      if (!isDragging) {
        const cardWidth = 320 + 20; // card width + gap
        const currentScroll = slider.scrollLeft;
        const newSlide = Math.round(currentScroll / (cardWidth * 3));
        setCurrentSlide(Math.max(0, newSlide));
      }
    };

    slider.addEventListener('scroll', handleScroll);
    return () => slider.removeEventListener('scroll', handleScroll);
  }, [isDragging]);

  // Mouse/Touch event handlers for dragging
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    const newScrollLeft = scrollLeft - walk;
    sliderRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, sliderRef.current.scrollWidth - sliderRef.current.clientWidth));
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    const newScrollLeft = scrollLeft - walk;
    sliderRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, sliderRef.current.scrollWidth - sliderRef.current.clientWidth));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Navigation functions
  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
  };

  // Helper function to get filtered practitioners with valid status
  const getValidPractitioners = () => {
    return displayedPractitioners.filter(practitioner => 
      practitioner.status === true || practitioner.status === false
    );
  };

  const nextSlide = () => {
    const validPractitioners = getValidPractitioners();
    const totalSlides = Math.ceil(validPractitioners.slice(0, 15).length / 3);
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    const validPractitioners = getValidPractitioners();
    const totalSlides = Math.ceil(validPractitioners.slice(0, 15).length / 3);
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  return (
    <div>
      <Navbar />
      {/* Header */}
      <header>
        <div className="find-practitioner-hero">
          <div className="find-practitioner-hero-content">
            <div className="hero-text-section">
              <h1>
                Find Trusted <span>Healthcare Practitioners</span> Near You &<br />
                <span>Book Appointments</span> with Ease!
              </h1>
            </div>
            
            {/* Enhanced Recommended Doctor Slider */}
            {(() => {
              // Filter practitioners with valid status (true or false, not null/undefined)
              const validPractitioners = displayedPractitioners.filter(practitioner => 
                practitioner.status === true || practitioner.status === false
              );
              
              return validPractitioners.length > 0 && (
              <div className="hero-recommended-doctor">
                <div className="hero-recommended-header">
                  <h3>Recommended Practitioners</h3>
                </div>
                <div 
                  className="hero-slider-container"
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                >
                  <button 
                    className="slider-nav-btn slider-prev-btn"
                    onClick={prevSlide}
                    aria-label="Previous slide"
                  >
                    <FaChevronLeft />
                  </button>
                  
                  <div 
                    className="hero-doctor-slider"
                    ref={sliderRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                      {validPractitioners.slice(0, 15).map((practitioner, index) => (
                      <div key={practitioner._id} className="hero-doctor-card">
                        {/* Instant Book Badge */}
                        {practitioner.status === true && (
                          <div className="instant-book-badge">
                            ⚡ Instant Book
                          </div>
                        )}
                        {/* Approval Required Badge */}
                        {practitioner.status === false && (
                          <div className="approval-required-badge">
                            ⏳ Approval Required
                          </div>
                        )}
                        <img
                          src={practitioner.profile_picture_url || process.env.PUBLIC_URL + "/assets/Virtual Triage.png"}
                          alt={practitioner.full_name}
                          className="hero-doctor-img"
                          onError={e => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/assets/Virtual Triage.png"; }}
                        />
                        <div className="hero-doctor-info">
                          <h4 className="hero-doctor-name">{practitioner.full_name}</h4>
                          <p className="hero-doctor-specialty">{practitioner.subspecialties?.[0] || 'General Practitioner'}</p>
                          <div className="hero-doctor-rating">
                            <Rating value={practitioner.rating || 0} />
                            <span className="hero-doctor-experience">{practitioner.experience_years || '5'}+ years experience</span>
                          </div>
                          <div className="hero-doctor-actions">
                            <button 
                              className="hero-doctor-btn hero-view-btn"
                              onClick={() => handlePractitionerClick(practitioner._id)}
                            >
                              <FaUserMd className="icon-margin-right"/>View Profile
                            </button>
                            {practitioner.status === true ? (
                              <button 
                                className="hero-doctor-btn hero-book-btn"
                                onClick={() => handleBookAppointment(practitioner)}
                              >
                                <FaCalendarCheck className="icon-margin-right"/>Book Now
                              </button>
                            ) : practitioner.status === false ? (
                              <button 
                                className="hero-doctor-btn hero-book-btn"
                                onClick={() => {
                                  setAppointmentRequestDoctor(practitioner);
                                  setAppointmentRequestModalOpen(true);
                                }}
                              >
                                <FaCalendarCheck className="icon-margin-right"/>Book Now
                              </button>
                            ) : practitioner.status === null || practitioner.status === undefined ? (
                              <button 
                                className="hero-doctor-btn hero-invite-btn"
                                onClick={() => {
                                  setInviteDoctor(practitioner);
                                  setInviteModalOpen(true);
                                }}
                              >
                                <FaUserPlus className="icon-margin-right" />Invite
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    className="slider-nav-btn slider-next-btn"
                    onClick={nextSlide}
                    aria-label="Next slide"
                  >
                    <FaChevronRight />
                  </button>
                </div>
                
                {/* Slider Dots */}
                <div className="slider-dots">
                  {Array.from({ length: Math.max(1, Math.ceil(validPractitioners.slice(0, 15).length / 3)) }, (_, index) => (
                    <button
                      key={index}
                      className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            );
            })()}
          </div>
        </div>
        {/* Search Bar Styled Like Screenshot */}
        <div className="search-bar-hero">
          <div className="search-bar-row">
            <div className={`search-input-container ${activeSearch === 'name' ? 'active' : ''} ${isLoading.name ? 'loading' : ''}`}>
                  <input
                    type="text"
                    placeholder="Search by Name"
                className="search-bar-input"
                value={searchQuery.name}
                onChange={(e) => handleSearch('name', e.target.value)}
                onClick={() => handleSearchFieldClick('name')}
              />
              {activeSearch === 'name' && searchResults.name.length > 0 && (
                <div className="search-results">
                  {searchResults.name.map(doc => renderPractitionerCard(doc))}
                </div>
              )}
            </div>
            <div className={`search-input-container ${activeSearch === 'location' ? 'active' : ''} ${isLoading.location ? 'loading' : ''}`}>
                  <input
                    type="text"
                    placeholder="Search by Location"
                className="search-bar-input"
                value={searchQuery.location}
                onChange={(e) => handleSearch('location', e.target.value)}
                onClick={() => handleSearchFieldClick('location')}
              />
              {activeSearch === 'location' && searchResults.location.length > 0 && (
                <div className="search-results">
                  {searchResults.location.map(doc => renderPractitionerCard(doc))}
                    </div>
                  )}
            </div>
            <div className={`search-input-container ${activeSearch === 'specialty' ? 'active' : ''} ${isLoading.specialty ? 'loading' : ''}`}>
                  <input
                    type="text"
                placeholder="Search by Speciality"
                className="search-bar-input"
                value={searchQuery.specialty}
                onChange={(e) => handleSearch('specialty', e.target.value)}
                onClick={() => handleSearchFieldClick('specialty')}
              />
              {activeSearch === 'specialty' && searchResults.specialty.length > 0 && (
                <div className="search-results">
                  {searchResults.specialty.map(doc => renderPractitionerCard(doc))}
                    </div>
                  )}
            </div>
          </div>
          <button
            className="search-bar-btn"
                              onClick={() => {
              if (loadMoreRef.current) {
                loadMoreRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            View All Practitioners
          </button>
                              </div>
      </header>

      {/* Main Content */}
      <div className="main-content-wrapper">
        {/* Sidebar Overlay */}
        <div 
          className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
          onClick={handleOverlayClick}
        />
        
        {/* Sidebar */}
        <aside className={`main-sidebar ${isSidebarOpen ? 'active' : ''}`}>
          <div className="sidebar-section sidebar-get-started-section">
            <div className="sidebar-info-container">
              <div className="sidebar-info-title">
                Not sure where to start?
              </div>
              <div className="sidebar-info-subtitle">
                Click here to get started!
              </div>
            </div>
            <button
              className="get-started-btn"
              onClick={openLargeChat}
            >
              <FaComments className="get-started-icon" />
              Get Started
            </button>
          </div>
          {/* Get Started Button */}
          
          <div className="sidebar-section">
            <div className="sidebar-title">SPECIALTIES</div>
            {["Dermatology", "Dental", "Veterinary", "Neurology", "Chiropractic", "GP"].map((s) => (
                <button
                key={s}
                className={`sidebar-btn${selectedSpecialty === s ? ' active' : ''}`}
                onClick={() => handleSpecialtySelect(s)}
                >
                {s}
                </button>
            ))}
          </div>
          <div className="sidebar-section">
            <div className="sidebar-title">LOCATION</div>
            {Object.keys(countryCities).map((country) => (
              <button 
                key={country}
                className={`sidebar-btn ${selectedLocation === country ? 'active' : ''}`}
                onClick={() => handleLocationSelect(country)}
              >
                {country}
              </button>
            ))}
          </div>
          <div className="sidebar-section">
            <div className="sidebar-title">RATINGS</div>
            {[5, 4, 3].map((r) => (
              <button
                key={r}
                className={`sidebar-btn${selectedRating === r ? ' active' : ''}`}
                onClick={() => handleRatingSelect(r)}
              >
                {r}+ Star Ratings
              </button>
            ))}
          </div>
          <div className="sidebar-section">
            <div className="sidebar-title">INSURANCE</div>
            <button
              className={`sidebar-btn${selectedInsurance ? ' active' : ''}`}
              onClick={handleInsuranceSelect}
            >
              Accept Insurance
            </button>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-title">LANGUAGE</div>
            {["English", "Spanish", "Arabic", "Urdu", "French", "German", "Russian"].map((l) => (
              <button
                key={l}
                className={`sidebar-btn${selectedLanguage === l ? ' active' : ''}`}
                onClick={() => handleLanguageSelect(l)}
              >
                {l}
              </button>
            ))}
          </div>
        </aside>

        {/* Practitioner Cards */}
        <main className="main-content-grid">
          {isLoading.initial ? (
            <div className="custom-loader-container">
              <div className="custom-loader-bg">
                <LogoCopy className="custom-loader-img custom-loader-logo" />
              </div>
            </div>
          ) : displayedPractitioners.length > 0 ? (
            <>
              {displayedPractitioners.map(doc => renderPractitionerCard(doc))}
              {pagination.has_next && (
                <div
                  className="load-more-container"
                  ref={loadMoreRef}
                >
                  <button
                    className="search-bar-btn"
                    onClick={handleLoadMore}
                    disabled={isLoading.loadMore}
                  >
                    {isLoading.loadMore ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">No practitioners found</div>
          )}
        </main>

        {/* Filter Toggle Button */}
        <button 
          className="filter-toggle-btn"
          onClick={toggleSidebar}
          aria-label="Toggle filters"
        >
          <FaFilter />
        </button>
      </div>

      {/* Why Virtual Healthcare Matters Section */}
      <section className="why-listing-section">
        <div className="why-listing-container">
          <div className="why-listing-right">
            <img src={process.env.PUBLIC_URL + "/assets/Asset 5.png"} alt="Why Virtual Healthcare Matters" className="why-listing-img" />
          </div>
          <div className="why-listing-left">
            <h2 className="why-listing-title">Why Virtual Healthcare Matters?</h2>
            <p className="why-listing-desc">
              Accessing quality healthcare should be convenient, cost-effective, and available anywhere. Virtual healthcare bridges the gap by eliminating long wait times, reducing travel hassles, and ensuring that patients receive expert consultation anytime, anywhere.
            </p>
            <ul className="why-listing-list">
              <li><FaCheckCircle className="why-listing-check"/> <b>Skip long queues & waiting rooms</b></li>
              <li><FaCheckCircle className="why-listing-check"/> <b>Get instant access to top specialists</b></li>
              <li><FaCheckCircle className="why-listing-check"/> <b>Affordable care with no hidden costs</b></li>
              <li><FaCheckCircle className="why-listing-check"/> <b>Secure video consultations & e-prescriptions</b></li>
            </ul>
          </div>
        </div>
      </section>
      <section className="cta">
        <div className="cta-container">
          <div className="cta-content">
            <h2 className="cta-title">Your Healthcare, Your Terms</h2>
            <p className="cta-description">Whether it's a late-night consultation, a second opinion, or urgent medical advice, Virtual Triage ensures that healthcare is always within your reach.</p>
            <a href="https://virtualtriage.co/home-page" className="cta-link">
              <button className="cta-button">
                <span>Get Started Now!</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </a>
          </div>
          <div className="cta-background">
            <div className="cta-pattern"></div>
          </div>
        </div>
      </section>
      <InviteModal 
        isOpen={inviteModalOpen} 
        onClose={() => setInviteModalOpen(false)} 
        doctorName={inviteDoctor?.full_name} 
        doctorEmail={inviteDoctor?.email} 
      />
      <AppointmentRequestModal 
        isOpen={appointmentRequestModalOpen} 
        onClose={() => setAppointmentRequestModalOpen(false)} 
        doctorId={appointmentRequestDoctor?._id}
        doctorName={appointmentRequestDoctor?.full_name} 
        doctorEmail={appointmentRequestDoctor?.email} 
      />
      <BookAppointmentModal 
        isOpen={bookAppointmentModalOpen} 
        onClose={() => setBookAppointmentModalOpen(false)} 
        doctorName={selectedDoctor?.full_name}
        doctorId={selectedDoctor?._id}
      />
      <LoginModal 
        isOpen={loginModalOpen} 
        onClose={() => {
          setLoginModalOpen(false);
          setPractitionerToRedirect(null);
        }} 
        onLoginSuccess={() => {
          if (practitionerToRedirect) {
            handleLoginSuccess(practitionerToRedirect);
            setPractitionerToRedirect(null);
          }
        }}
      />
      <Footer />
    </div>
  );
};

export default FindPractitioners;